/**
 * User Block Component
 */
import React, { useEffect, useMemo, useState } from "react";

import { useMutation } from "@tanstack/react-query";
import { Company } from "Models/company";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import Leave from "../../../components/Generic/Modal/Leave";
import LoadingScreen from "../../../components/LoadingScreen";
import {
  SELECTED_COMPANIES_IDS,
  SELECTED_TASK_TYPES,
  USER_COMPANY,
  USER_TOKEN,
  USER_UID,
} from "../../../constants";
import { useAppState } from "../../../context/StateContext";
import useCurrentBrand from "../../../hooks/useCurrentBrand";
import { Resources } from "../../../models/resources";
import { User } from "../../../models/user";
import UserService from "../../../services/user.service";
import { getBrandByCompanyName } from "../../../util/brands";
import { ResourceGroup } from "../../../util/enums";
import { UserMenuIcon, UserMenuIconImage } from "./styles";
import { UserBlockCompanyMenu } from "./UserBlockCompanyMenu";
import { UserBlockProfileMenu } from "./UserBlockProfileMenu";

const UserBlock = () => {
  const {
    currentUser,
    setCurrentUser,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    setHasUserChanged,
    setLoginTime,
  } = useAppState();

  const [userProfileMenu, setUserProfileMenu] = useState<null | HTMLElement>(null);
  const [userCompanyMenu, setUserCompanyMenu] = useState<null | HTMLElement>(null);
  const [userToSwitch, setUserToSwitch] = useState<User>();
  const [billingIsAllowed, setBillingIsAllowed] = useState<boolean>(false);

  const [isSwitching, setIsSwitching] = useState<boolean>(false);

  const { t: translate } = useTranslation();

  const { setCurrentBrand } = useCurrentBrand();

  const history = useHistory();

  const isBrandingEnabled = process.env.IS_BRANDING_ENABLED === "true";

  const { mutate: switchCompany } = useMutation(
    ["user.company.switch"],
    (user: { id: number; companyId: number }) => UserService.switchCompany(user.id, user.companyId),
    {
      onSuccess: (response) => {
        if (response.data) {
          const { name, id } = response.data.user.company;

          if (isBrandingEnabled) setCurrentBrand(getBrandByCompanyName(id!));

          localStorage.setItem(USER_COMPANY, JSON.stringify(response.data.user.company));
          localStorage.setItem(USER_TOKEN, response.data.token);
          localStorage.setItem(USER_UID, response.data.token);
          localStorage.setItem(
            SELECTED_COMPANIES_IDS,
            JSON.stringify([response.data.user.company.id])
          );
          localStorage.removeItem(SELECTED_TASK_TYPES);
          setCurrentUser(response.data.user);
        }

        setUserToSwitch(undefined);
        setUserCompanyMenu(null);
        setUserProfileMenu(null);
        setHasUserChanged(true);
        location.reload();
        location.replace("/");
      },
    }
  );

  const { mutate: refreshToken } = useMutation(
    ["user.refresh.token"],
    (user: { email: string }) => UserService.refreshToken(user.email),
    {
      onSuccess: (response) => {
        if (response && response.data && response.data.token) {
          localStorage.setItem(USER_TOKEN, response.data.token);
        }
      },
    }
  );

  useEffect(() => {
    refreshToken({ email: currentUser?.email ?? "" });
    setLoginTime();
  }, [currentUser?.companyId]);

  useEffect(() => {
    checkIfBillingIsAllowed();
  }, [currentUser]);

  const checkIfBillingIsAllowed = () => {
    let isAllowed = false;

    if (currentUser?.resources) {
      const resourceBilling: Resources | undefined = currentUser?.resources?.find(
        (resource) =>
          resource?.group?.toLowerCase() === ResourceGroup.BILLING.toLowerCase() &&
          resource?.roles_resources?.view
      );

      if (resourceBilling) isAllowed = true;
    }

    setBillingIsAllowed(isAllowed);
  };

  useEffect(() => {
    checkIfBillingIsAllowed();
  }, [currentUser]);

  const avatarUrl = useMemo((): string => {
    if (currentUser?.picture?.mediaLink) return currentUser?.picture.mediaLink;

    // eslint-disable-next-line global-require
    return require("Assets/profile.png");
  }, [currentUser]);

  const handleLeave = (isConfirmed: boolean) => {
    if (isConfirmed) {
      setHasUnsavedChanges(false);
      if (userToSwitch?.id && userToSwitch?.companyId) {
        switchCompany({ id: userToSwitch?.id, companyId: userToSwitch?.companyId });
        setIsSwitching(true);
      }

      return;
    }
    setUserToSwitch(undefined);
  };

  const handleOpenCompanyMenu = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setUserCompanyMenu(event.currentTarget);
  };

  const handleOpenUserProfileMenu = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setUserProfileMenu(event.currentTarget);
  };

  const handleCloseCompanyMenu = () => {
    setUserCompanyMenu(null);
  };

  const handleCloseProfileMenu = () => {
    setUserProfileMenu(null);
  };

  const handleSwitchCompany = (company: Company) => {
    if (!currentUser?.id || !company.id) return;

    if (hasUnsavedChanges) {
      setUserToSwitch({ id: currentUser.id, companyId: company.id } as User);

      return;
    }

    switchCompany({ id: currentUser.id, companyId: company.id });
    setIsSwitching(true);
  };

  return (
    <div data-cy="userProfileMenu">
      <LoadingScreen showing={isSwitching} message={translate("switchingAccounts")} />

      <Leave open={!!userToSwitch} onClose={handleLeave} />

      <UserMenuIcon onClick={handleOpenUserProfileMenu}>
        <UserMenuIconImage src={avatarUrl} alt="user profile" />
      </UserMenuIcon>

      <UserBlockProfileMenu
        billingIsAllowed={billingIsAllowed}
        handleOpenCompanyMenu={handleOpenCompanyMenu}
        handleCloseProfileMenu={handleCloseProfileMenu}
        userProfileMenu={userProfileMenu}
        userCompanyMenu={userCompanyMenu}
        avatarUrl={avatarUrl}
      />

      <UserBlockCompanyMenu
        loadingSwitchCompany={isSwitching}
        userCompanyMenu={userCompanyMenu}
        handleCloseCompanyMenu={handleCloseCompanyMenu}
        handleSwitchCompany={handleSwitchCompany}
      />
    </div>
  );
};

export default UserBlock;
